import moment from 'moment-timezone';

export const VOICE_WAVEFORM_BAR_COUNT = 40;

const MINIMUM_BAR_LEVEL = 0.08;
const SILENCE_FLOOR_DB = -50;
const LOUD_CEILING_DB = -5;

export function computePeaksFromSamples(
  samples: Float32Array,
  barCount: number = VOICE_WAVEFORM_BAR_COUNT,
): number[] {
  if (samples.length === 0) return Array(barCount).fill(MINIMUM_BAR_LEVEL);

  const bucketSize = samples.length / barCount;
  const peaks: number[] = [];
  for (let bar = 0; bar < barCount; bar++) {
    const start = Math.floor(bar * bucketSize);
    const end = Math.max(start + 1, Math.floor((bar + 1) * bucketSize));
    let sumOfSquares = 0;
    for (let index = start; index < end; index++) {
      sumOfSquares += samples[index] * samples[index];
    }
    peaks.push(Math.sqrt(sumOfSquares / (end - start)));
  }
  return normalizePeaks(peaks);
}

export function resamplePeaks(
  levels: number[],
  barCount: number = VOICE_WAVEFORM_BAR_COUNT,
): number[] {
  if (levels.length === 0) return Array(barCount).fill(MINIMUM_BAR_LEVEL);

  const bucketSize = levels.length / barCount;
  const peaks: number[] = [];
  for (let bar = 0; bar < barCount; bar++) {
    const start = Math.floor(bar * bucketSize);
    const end = Math.max(start + 1, Math.floor((bar + 1) * bucketSize));
    peaks.push(Math.max(...levels.slice(start, Math.min(end, levels.length))));
  }
  return normalizePeaks(peaks);
}

export function convertWaveformToPeaks(
  waveform: number[] | null | undefined,
  barCount: number = VOICE_WAVEFORM_BAR_COUNT,
): number[] | null {
  if (!waveform?.length) return null;
  return resamplePeaks(
    waveform.map((percentOfLoudest) => percentOfLoudest / 100),
    barCount,
  );
}

export function measureLevelFromTimeDomain(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sumOfSquares = 0;
  for (const sample of samples) {
    sumOfSquares += sample * sample;
  }
  const rootMeanSquare = Math.sqrt(sumOfSquares / samples.length);
  if (rootMeanSquare === 0) return 0;
  const decibels = 20 * Math.log10(rootMeanSquare);
  return clamp(
    (decibels - SILENCE_FLOOR_DB) / (LOUD_CEILING_DB - SILENCE_FLOOR_DB),
    0,
    1,
  );
}

export function formatPlaybackTime(seconds: number | null | undefined): string {
  const safeSeconds =
    seconds != null && Number.isFinite(seconds) && seconds > 0
      ? Math.floor(seconds)
      : 0;
  const duration = moment.duration(safeSeconds, 'seconds');
  const minutes = Math.floor(duration.asMinutes());
  return `${String(minutes).padStart(2, '0')}:${String(duration.seconds()).padStart(2, '0')}`;
}

function normalizePeaks(peaks: number[]): number[] {
  const loudest = Math.max(...peaks);
  if (loudest <= 0) return peaks.map(() => MINIMUM_BAR_LEVEL);
  return peaks.map((peak) =>
    Math.max(MINIMUM_BAR_LEVEL, Math.min(1, peak / loudest)),
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
